import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';
import type { Stadt, CreateStadtInput, UpdateStadtInput } from '../types/stadt';

// Query keys
export const stadtKeys = {
  all: ['stadt'] as const,
  lists: () => [...stadtKeys.all, 'list'] as const,
  list: (activeOnly?: boolean, search?: string) => [...stadtKeys.lists(), { activeOnly, search }] as const,
  details: () => [...stadtKeys.all, 'detail'] as const,
  detail: (id: string) => [...stadtKeys.details(), id] as const,
};

interface ApiResponse<T> {
  success: boolean;
  data: T;
}

// API functions
const fetchStadtList = async (activeOnly?: boolean, search?: string): Promise<ApiResponse<Stadt[]>> => {
  const params = new URLSearchParams();
  if (activeOnly !== undefined) params.append('activeOnly', String(activeOnly));
  if (search) params.append('search', search);

  const response = await apiClient.get(`/stadt?${params.toString()}`);
  return response.data;
};

const fetchStadt = async (id: string): Promise<ApiResponse<Stadt>> => {
  const response = await apiClient.get(`/stadt/${id}`);
  return response.data;
};

const createStadt = async (data: CreateStadtInput): Promise<ApiResponse<Stadt>> => {
  const response = await apiClient.post('/stadt', data);
  return response.data;
};

const updateStadt = async ({ id, data }: { id: string; data: UpdateStadtInput }): Promise<ApiResponse<Stadt>> => {
  const response = await apiClient.patch(`/stadt/${id}`, data);
  return response.data;
};

const deleteStadt = async (id: string): Promise<void> => {
  await apiClient.delete(`/stadt/${id}`);
};

// Hooks
export function useStadtList(activeOnly?: boolean, search?: string) {
  return useQuery({
    queryKey: stadtKeys.list(activeOnly, search),
    queryFn: () => fetchStadtList(activeOnly, search),
    select: (response) => response.data,
  });
}

export function useStadt(id: string) {
  return useQuery({
    queryKey: stadtKeys.detail(id),
    queryFn: () => fetchStadt(id),
    select: (response) => response.data,
    enabled: !!id,
  });
}

export function useCreateStadt() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createStadt,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: stadtKeys.lists() });
    },
  });
}

export function useUpdateStadt() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateStadt,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: stadtKeys.lists() });
      queryClient.invalidateQueries({ queryKey: stadtKeys.detail(variables.id) });
    },
  });
}

export function useDeleteStadt() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteStadt,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: stadtKeys.lists() });
    },
  });
}
